import React, { useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';

import { formatRelativeTime } from '~/pages/AskRichardUnified/chat/SessionHistoryPanel';

import type { Lens, LensResponse } from '~/types/leLens';

import { ownerDisplayName } from '../lensLibrary';

import LensViewersDialog from './LensViewersDialog';

type FactsProps = {
  lens: Pick<Lens, 'ownerEmail' | 'updatedAt'>;
  attribution?: LensResponse['attribution'];
  viewCount?: LensResponse['viewCount'];
};

type Props = FactsProps & {
  lens: Pick<Lens, 'id' | 'title' | 'ownerEmail' | 'updatedAt'>;
  onHistory?: () => void;
};

export function LensAttributionFacts({ lens, attribution, viewCount }: FactsProps) {
  const date = new Date(attribution?.lastEditedAt ?? lens.updatedAt);
  return (
    <Stack spacing={1}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Lens details</Typography>
      <Box>
        <Typography color="text.secondary" sx={{ fontSize: 11 }}>
          {attribution?.createdBy ? 'Created by' : 'Current owner'}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>{ownerDisplayName(attribution?.createdBy || lens.ownerEmail)}</Typography>
        {!attribution?.createdBy && (
          <Typography color="text.secondary" sx={{ fontSize: 11 }}>
            The original creator was not recorded.
          </Typography>
        )}
      </Box>
      {!Number.isNaN(date.getTime()) && (
        <Box>
          <Typography color="text.secondary" sx={{ fontSize: 11 }}>
            {attribution?.lastEditedAt ? 'Last edited' : 'Last updated'}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {date.toLocaleString('en-AU', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Typography>
          {attribution?.lastEditedBy && (
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              by {ownerDisplayName(attribution.lastEditedBy)}
            </Typography>
          )}
        </Box>
      )}
      {viewCount && (
        <Box>
          <Typography color="text.secondary" sx={{ fontSize: 11 }}>
            Views
          </Typography>
          <LensViewLines viewCount={viewCount} />
        </Box>
      )}
    </Stack>
  );
}

export function LensViewLines({ viewCount }: { viewCount: NonNullable<LensResponse['viewCount']> }) {
  return (
    <>
      <Typography sx={{ fontSize: 13 }}>
        {viewCount.total} in 30 days · {viewCount.lastSevenDays} in the last 7
      </Typography>
      {viewCount.yours > 0 && (
        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
          You opened it {viewCount.yours === 1 ? 'once' : `${viewCount.yours} times`}
        </Typography>
      )}
      {Boolean(viewCount.people) && (
        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
          By {viewCount.people} {viewCount.people === 1 ? 'person' : 'people'}
          {viewCount.lastViewedAt ? ` · last ${formatRelativeTime(viewCount.lastViewedAt, Date.now())}` : ''}
        </Typography>
      )}
    </>
  );
}

export function LensViewFacts({
  lens,
  viewCount,
}: {
  lens: Pick<Lens, 'id' | 'title' | 'ownerEmail'>;
  viewCount: NonNullable<LensResponse['viewCount']>;
}) {
  const [viewersOpen, setViewersOpen] = useState(false);
  return (
    <>
      <LensViewLines viewCount={viewCount} />
      {viewCount.people !== undefined && (
        <Button
          size="small"
          onClick={() => setViewersOpen(true)}
          sx={{ mt: 1, px: 0, py: 0, minHeight: 24, fontSize: 12 }}
        >
          Who viewed
        </Button>
      )}
      {viewersOpen && <LensViewersDialog lens={lens} open={viewersOpen} onClose={() => setViewersOpen(false)} />}
    </>
  );
}

/** Compact disclosure beside the header actions; author metadata stays out of the title and thumbnail. */
export default function LensHeaderAttribution(props: Props) {
  const { lens, onHistory, viewCount } = props;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [viewersOpen, setViewersOpen] = useState(false);
  return (
    <>
      <Tooltip title="Lens details">
        <IconButton
          aria-expanded={Boolean(anchor)}
          aria-haspopup="dialog"
          aria-label="Lens details"
          onClick={(event) => setAnchor(event.currentTarget)}
          size="small"
          sx={{ color: 'inherit', opacity: 0.72 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '8px', p: 1.5, width: 240, mt: 0.75 } } }}
      >
        <Box role="dialog" aria-label="Lens details">
          <LensAttributionFacts {...props} />
          <Stack direction="row" spacing={2}>
            {onHistory && (
              <Button
                size="small"
                onClick={() => {
                  setAnchor(null);
                  onHistory?.();
                }}
                sx={{ mt: 1, px: 0, py: 0, minHeight: 24, fontSize: 12 }}
              >
                Version history
              </Button>
            )}
            {viewCount?.people !== undefined && (
              <Button
                size="small"
                onClick={() => {
                  setAnchor(null);
                  setViewersOpen(true);
                }}
                sx={{ mt: 1, px: 0, py: 0, minHeight: 24, fontSize: 12 }}
              >
                Who viewed
              </Button>
            )}
          </Stack>
        </Box>
      </Popover>
      {viewersOpen && <LensViewersDialog lens={lens} open={viewersOpen} onClose={() => setViewersOpen(false)} />}
    </>
  );
}
